<script setup>
import { computed, ref } from 'vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  inbox: { type: Object, default: () => ({}) },
  appliedFiltersCount: { type: Number, default: 0 },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'createEvolutionGroup',
  'syncEvolutionContacts',
  'syncEvolutionHistory',
]);

const { isAdmin } = useAdmin();
const { t } = useI18n();
const showMoreMenu = ref(false);

const canCreateEvolutionGroup = computed(
  () =>
    props.inbox?.provider === 'evolution' &&
    !!props.inbox?.evolution_groups_enabled
);

const canSyncEvolutionContacts = computed(
  () => props.inbox?.provider === 'evolution'
);

// Admin only, matching InboxPolicy#evolution_manual_sync? - backfilling a week of every chat is
// a heavier call than importing contacts.
const canSyncEvolutionHistory = computed(
  () => props.inbox?.provider === 'evolution' && isAdmin.value
);

const { uiSettings, updateUISettings } = useUISettings();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};

// Everything that is not "filter" lives behind one overflow button: the header
// column is 370px wide and seven icons of equal weight read as noise.
const moreMenuItems = computed(() => {
  const items = [];

  if (props.hasAppliedFilters && !props.hasActiveFolders) {
    items.push({
      label: t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON'),
      value: 'addFolders',
      action: 'addFolders',
      icon: 'i-lucide-save',
    });
    items.push({
      label: t('FILTER.CLEAR_BUTTON_LABEL'),
      value: 'resetFilters',
      action: 'resetFilters',
      icon: 'i-lucide-circle-x',
    });
  }
  if (props.hasActiveFolders) {
    items.push(
      {
        label: t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON'),
        value: 'filtersModal',
        action: 'filtersModal',
        icon: 'i-lucide-pen-line',
      },
      {
        label: t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON'),
        value: 'deleteFolders',
        action: 'deleteFolders',
        icon: 'i-lucide-trash-2',
      }
    );
  }
  if (canCreateEvolutionGroup.value) {
    items.push({
      label: t('CHAT_LIST.CREATE_EVOLUTION_GROUP'),
      value: 'createEvolutionGroup',
      action: 'createEvolutionGroup',
      icon: 'i-lucide-users-round',
    });
  }
  if (canSyncEvolutionContacts.value) {
    items.push({
      label: t('CHAT_LIST.SYNC_EVOLUTION_CONTACTS'),
      value: 'syncEvolutionContacts',
      action: 'syncEvolutionContacts',
      icon: 'i-lucide-contact',
    });
  }
  if (canSyncEvolutionHistory.value) {
    items.push({
      label: t('CHAT_LIST.SYNC_EVOLUTION_HISTORY'),
      value: 'syncEvolutionHistory',
      action: 'syncEvolutionHistory',
      icon: 'i-lucide-history',
    });
  }
  items.push({
    label: t('CONVERSATION.SWITCH_VIEW_LAYOUT'),
    value: 'toggleLayout',
    action: 'toggleLayout',
    icon: props.isOnExpandedLayout
      ? 'i-lucide-panel-left'
      : 'i-lucide-layout-grid',
  });

  return items;
});

const onMoreMenuAction = ({ action }) => {
  showMoreMenu.value = false;
  const handlers = {
    toggleLayout: toggleConversationLayout,
    addFolders: () => emit('addFolders'),
    resetFilters: () => emit('resetFilters'),
    filtersModal: () => emit('filtersModal'),
    deleteFolders: () => emit('deleteFolders'),
    createEvolutionGroup: () => emit('createEvolutionGroup'),
    syncEvolutionContacts: () => emit('syncEvolutionContacts'),
    syncEvolutionHistory: () => emit('syncEvolutionHistory'),
  };
  handlers[action]?.();
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 px-3 h-[3.25rem]"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center justify-center min-w-0">
      <h1
        class="text-base font-medium truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders"
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
    </div>
    <div class="flex gap-1 items-center">
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      <NextButton
        v-if="!hasActiveFolders"
        v-tooltip.top-end="$t('FILTER.TOOLTIP_LABEL')"
        icon="i-lucide-list-filter"
        :label="hasAppliedFilters ? String(appliedFiltersCount) : ''"
        :color="hasAppliedFilters ? 'blue' : 'slate'"
        xs
        faded
        @click="emit('filtersModal')"
      />
      <div v-on-click-outside="() => (showMoreMenu = false)" class="relative">
        <NextButton
          v-tooltip.top-end="$t('CHAT_LIST.MORE_ACTIONS')"
          icon="i-lucide-more-horizontal"
          slate
          xs
          faded
          @click="showMoreMenu = !showMoreMenu"
        />
        <DropdownMenu
          v-if="showMoreMenu"
          :menu-items="moreMenuItems"
          class="mt-1 top-full z-[100] w-56 ltr:right-0 rtl:left-0"
          @action="onMoreMenuAction"
        />
        <div
          id="saveFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
    </div>
  </div>
</template>
